import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CardSkeleton from '../../../utils/common/components/CardSkeleton';
import { getCompanyDetails, setCompanyID } from '../store/company.actions';
import {
  selectAllCompanies,
  selectAllCompaniesError,
  selectAllCompaniesStatus,
} from '../store/company.selectors';
import { fetchAllCollections } from '../../collection/store/collection.actions';

import CompanyCard from './CompanyCard';
import UsersModal from './UsersModal';

import { PageWrapper, TableWrapper, StyledPaper } from '../../../utils/common/styles/commonStyles';

import PropTypes from 'prop-types';

const CompanyModel = ({ handleOpenModal, searchTerm = '' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const companies = useSelector(selectAllCompanies);
  const status = useSelector(selectAllCompaniesStatus);
  const error = useSelector(selectAllCompaniesError);

  const [selectedCompanyForUsers, setSelectedCompanyForUsers] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleCompanyClick = (row) => {
    dispatch(setCompanyID(row.id));
    dispatch(fetchAllCollections(row.id));
    navigate('/collections');
  };

  const filteredAndSortedCompanies = useMemo(() => {
    let filtered = companies;
    if (searchTerm && searchTerm.trim() !== '') {
      const lowerSearchTerm = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((comp) => {
        const searchableFields = [comp.companyName];
        return searchableFields.some((field) =>
          String(field).toLowerCase().includes(lowerSearchTerm)
        );
      });
    }
    return filtered;
  }, [companies, searchTerm]);

  if (status === 'loading') {
    return <CardSkeleton count={6} />;
  }

  if (status === 'fail') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <PageWrapper>
      <TableWrapper>
        <StyledPaper sx={{ minHeight: '70vh', p: 2 }}>
          <Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr 1fr',
                  md: 'repeat(3, 1fr)',
                },
                gap: 2,
                padding: 2,
                borderRadius: 1,
              }}
            >
              {filteredAndSortedCompanies?.length ? (
                filteredAndSortedCompanies.map((row) => (
                  <CompanyCard
                    key={row.id}
                    company={row}
                    onClick={() => {
                      handleCompanyClick(row);
                      localStorage.setItem('CompanyData', JSON.stringify(row));
                    }}
                    onEdit={() => {
                      dispatch(getCompanyDetails(row.id));
                      handleOpenModal('edit');
                    }}
                    onManageUsers={() => setSelectedCompanyForUsers(row.id)}
                    onViewDetails={() => {
                      dispatch(getCompanyDetails(row.id));
                      handleOpenModal('view');
                    }}
                  />
                ))
              ) : (
                <Box sx={{ gridColumn: '1 / -1', p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                    No companies available.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </StyledPaper>
      </TableWrapper>

      <UsersModal
        open={Boolean(selectedCompanyForUsers)}
        companyId={selectedCompanyForUsers}
        onClose={() => setSelectedCompanyForUsers(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageWrapper>
  );
};

export default CompanyModel;

CompanyModel.propTypes = {
  handleOpenModal: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
};
